import React from "react";
import { Link } from "react-router-dom";
import { Ruler, Move3D, Eye } from "lucide-react";

const SHAPES = [
  { name: "Oval", description: "Balanced contour. Choose any silhouette.", recommend: ["The Capital", "The Ascent"] },
  { name: "Round", description: "Softer profile. Choose angular frames to define.", recommend: ["The Capital", "The Boardroom"] },
  { name: "Square", description: "Strong jaw. Choose softer, sculpted frames.", recommend: ["The Muse", "The Ascent"] },
  { name: "Heart", description: "Wider forehead. Choose bottom-heavy silhouettes.", recommend: ["The Boardroom", "The Muse"] },
  { name: "Rectangle", description: "Longer face. Choose wider, deeper frames.", recommend: ["The Runner", "The Fairway"] },
];

export default function FitGuide() {
  return (
    <div data-testid="fit-guide-page" className="bg-poche-black text-white">
      <section className="container-x py-20">
        <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Joseph Poche · The Fit Guide</p>
        <h1 className="font-serif text-6xl md:text-7xl mt-4 leading-[0.95]">Find your<br />contour.</h1>
        <p className="text-white/65 max-w-xl mt-6 leading-relaxed">
          Built For Real Faces is not a slogan. It is a measurement standard. Our frames begin at a 16–18mm
          bridge — wider than the eyewear industry default — and extend with a deeper temple contour.
          Here is how to find the silhouette engineered for you.
        </p>
      </section>

      {/* Measurement diagram */}
      <section className="container-x pb-20">
        <div className="bg-poche-ivory text-poche-black p-10 md:p-16">
          <p className="text-[11px] tracking-luxury uppercase text-poche-black/55">Frame Anatomy</p>
          <h2 className="font-serif text-4xl md:text-5xl mt-3">Five measurements that matter.</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mt-12">
            {[
              ["Frame Width", "142 – 150 mm", "The total horizontal span of the frame."],
              ["Lens Width", "52 – 58 mm", "Each individual lens opening."],
              ["Bridge Width", "16 – 18 mm", "The critical measurement — wider for real faces."],
              ["Temple Length", "142 – 148 mm", "From hinge to behind the ear."],
              ["Lens Height", "44 – 54 mm", "Vertical lens dimension."],
            ].map(([t, v, b], i) => (
              <div key={i} className="border-t border-poche-black/30 pt-5">
                <div className="text-[10px] tracking-luxury uppercase text-poche-black/55">{t}</div>
                <div className="font-serif text-2xl text-poche-gold mt-1">{v}</div>
                <p className="text-xs text-poche-black/65 mt-2 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Face shape guide */}
      <section className="container-x pb-20">
        <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Face Shape Guide</p>
        <h2 className="font-serif text-4xl md:text-5xl mt-3">Find your silhouette.</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-poche-gold/15 mt-10">
          {SHAPES.map((s) => (
            <div key={s.name} className="bg-poche-ink p-8" data-testid={`shape-${s.name.toLowerCase()}`}>
              <div className="font-serif text-3xl text-poche-gold">{s.name}</div>
              <p className="text-xs text-white/60 mt-3 leading-relaxed">{s.description}</p>
              <div className="text-[10px] tracking-luxury uppercase text-white/45 mt-6">Recommended</div>
              <ul className="text-sm text-white/80 mt-2 space-y-1">
                {s.recommend.map((r) => <li key={r}>· {r}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Wide-fit explanation */}
      <section className="bg-poche-ink border-t border-poche-gold/15">
        <div className="container-x py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { i: Move3D, t: "Why wider?", b: "A 16–18mm bridge accommodates a deeper, sculpted nose contour without sliding. The frame remains seated through expression, motion, and the long day." },
            { i: Ruler, t: "Why deeper?", b: "Deeper temple geometry distributes weight evenly across the temple and ear — eliminating the pressure point that defines lesser frames." },
            { i: Eye, t: "Why architectural?", b: "Real faces have presence. Our silhouettes are designed not to disappear into the face, but to hold space with it. Eyewear as architecture." },
          ].map((p, i) => (
            <div key={i} className="border-t border-poche-gold/30 pt-6">
              <p.i size={24} className="text-poche-gold" strokeWidth={1.4} />
              <h3 className="font-serif text-3xl mt-4">{p.t}</h3>
              <p className="text-white/65 mt-3 leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x py-20 text-center">
        <h3 className="font-serif text-4xl md:text-5xl">Still not sure?</h3>
        <p className="text-white/65 mt-4 max-w-xl mx-auto">Open the POCHE mobile app for our AI-powered face scan & personal fit profile.</p>
        <div className="mt-8 flex gap-4 justify-center">
          <Link to="/app/fit-profile" className="btn-gold">Open Face Scan</Link>
          <Link to="/contact" className="btn-outline-gold">Talk to Concierge</Link>
        </div>
      </section>
    </div>
  );
}
